/**
 * "Add video" from the game plan card (APS-259 step 4). Two choices:
 *  1. Paste a YouTube link. Saved to athlete_profiles.featured_video_url
 *     through PUT /profiles/athlete-profiles/:id (featuredVideoUrl).
 *  2. Connect YouTube (useSocialConnect), then upload from Messages.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Link2, X, Youtube } from 'react-feather';
import Modal from '../Modal';
import { Button, Field, TextInput, fieldAria } from '../ui';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { athleteAPI } from '../../services/api';
import analytics from '../../services/analytics';
import { checkVideoLink, youtubeThumbnail, VIDEO_ERROR_HUDL, VIDEO_ERROR_OTHER } from '../../utils/youtube';
import { useSocialConnect } from '../../hooks/useSocialConnect';
import {
  VIDEO_CONNECTED_LINE,
  VIDEO_CONNECT_LINE,
  VIDEO_CONNECT_TITLE,
  VIDEO_LINE,
  VIDEO_LINK_LINE,
  VIDEO_LINK_TITLE,
  cleanFirstName,
} from './gamePlanCopy';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  athleteId: string;
  /** false for a guardian; the title then names the athlete. */
  isSelf: boolean;
  athleteFirstName?: string | null;
  currentUrl?: string | null;
  /** From the game plan's video step. */
  youtubeConnected?: boolean;
  onSaved: (url: string) => void;
  /** After a YouTube connect attempt, so the caller can reload the plan. */
  onYoutubeChanged?: () => void;
}

const sectionStyle: React.CSSProperties = {
  border: '1px solid var(--aps-border)',
  borderRadius: 12,
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};
const sectionHead: React.CSSProperties = { display: 'flex', alignItems: 'flex-start', gap: 10 };
const sectionIcon: React.CSSProperties = { color: 'var(--aps-action-on-tint)', flexShrink: 0, marginTop: 2 };
const sectionTitle: React.CSSProperties = { margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--aps-ink)', lineHeight: 1.35 };
const sectionLine: React.CSSProperties = { margin: '2px 0 0', fontSize: 13, lineHeight: 1.45, color: 'var(--aps-text-2)' };

const AddVideoModal: React.FC<Props> = ({
  isOpen, onClose, athleteId, isSelf, athleteFirstName, currentUrl, youtubeConnected, onSaved, onYoutubeChanged,
}) => {
  const { sessionToken } = useAuth();
  const toast = useToast();
  const { connect, connecting } = useSocialConnect({ athleteId: athleteId || null, onConnected: onYoutubeChanged });
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setValue(currentUrl || '');
    setError(null);
    setSaving(false);
  }, [isOpen, currentUrl]);

  const check = checkVideoLink(value);
  const previewId = check.id || null;
  const first = cleanFirstName(athleteFirstName);
  const title = isSelf ? 'Add your highlight video' : `Add ${first ? `${first}'s` : "your athlete's"} highlight video`;

  const save = async () => {
    if (!sessionToken || !athleteId) return;
    const result = checkVideoLink(value);
    if (result.error) {
      setError(result.error);
      try { analytics.track('video_link_save_failed', { reason: result.reason }); } catch {}
      return;
    }
    if (!result.url) {
      setError(VIDEO_ERROR_OTHER);
      try { analytics.track('video_link_save_failed', { reason: 'empty' }); } catch {}
      return;
    }
    setSaving(true);
    try {
      await athleteAPI.updateProfile(sessionToken, athleteId, { featuredVideoUrl: result.url });
      toast.success('Video saved.');
      onSaved(result.url);
    } catch (err: any) {
      const msg: string = err?.message || '';
      // The server sends the same two messages for a bad link.
      if (msg === VIDEO_ERROR_HUDL || msg === VIDEO_ERROR_OTHER) {
        setError(msg);
        try { analytics.track('video_link_save_failed', { reason: 'server' }); } catch {}
      } else {
        toast.error(msg || 'Could not save the video. Try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      disableClose={saving}
      labelledById="add-video-title"
      contentStyle={{ maxWidth: 480, fontFamily: 'var(--aps-font-sans)' }}
    >
      <form
        onSubmit={(e) => { e.preventDefault(); save(); }}
        style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 id="add-video-title" style={{ margin: 0, fontSize: 19, fontWeight: 800, color: 'var(--aps-ink)' }}>
              {title}
            </h2>
            <p style={{ margin: '6px 0 0', fontSize: 14, lineHeight: 1.5, color: 'var(--aps-text-2)' }}>
              {VIDEO_LINE}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            aria-label="Close"
            style={{
              width: 34, height: 34, flexShrink: 0, borderRadius: 999, border: 'none',
              background: 'var(--aps-fill)', color: 'var(--aps-text-muted)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <X size={18} />
          </button>
        </div>

        <section style={sectionStyle} aria-labelledby="add-video-link-title">
          <div style={sectionHead}>
            <Link2 size={18} aria-hidden style={sectionIcon} />
            <div style={{ minWidth: 0 }}>
              <h3 id="add-video-link-title" style={sectionTitle}>{VIDEO_LINK_TITLE}</h3>
              <p style={sectionLine}>{VIDEO_LINK_LINE}</p>
            </div>
          </div>
          <Field id="add-video-url" label="YouTube link" error={error}>
            <TextInput
              {...fieldAria('add-video-url', { error })}
              invalid={!!error}
              type="url"
              inputMode="url"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              maxLength={500}
              placeholder="https://youtu.be/..."
              value={value}
              onChange={(e) => { setValue(e.target.value); if (error) setError(null); }}
            />
          </Field>

          {previewId && (
            <img
              src={youtubeThumbnail(previewId)}
              alt="Video preview"
              style={{
                width: '100%', aspectRatio: '16 / 9', objectFit: 'cover',
                borderRadius: 12, background: 'var(--aps-fill)', display: 'block',
              }}
            />
          )}

          <Button type="submit" variant="primary" loading={saving} style={{ alignSelf: 'flex-start' }}>
            Save link
          </Button>
        </section>

        <div aria-hidden style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--aps-text-muted)', fontSize: 13, fontWeight: 600 }}>
          <span style={{ flex: 1, height: 1, background: 'var(--aps-border)' }} />
          or
          <span style={{ flex: 1, height: 1, background: 'var(--aps-border)' }} />
        </div>

        <section style={sectionStyle} aria-labelledby="add-video-connect-title">
          <div style={sectionHead}>
            <Youtube size={18} aria-hidden style={sectionIcon} />
            <div style={{ minWidth: 0 }}>
              <h3 id="add-video-connect-title" style={sectionTitle}>{VIDEO_CONNECT_TITLE}</h3>
              <p style={sectionLine}>{youtubeConnected ? VIDEO_CONNECTED_LINE : VIDEO_CONNECT_LINE}</p>
            </div>
          </div>
          {youtubeConnected ? (
            <Link
              to="/messages?filter=youtube"
              onClick={() => {
                try { analytics.track('game_plan_video_upload_clicked', {}); } catch {}
                onClose();
              }}
              style={{ fontSize: 14, fontWeight: 700, color: 'var(--aps-action-on-tint)', alignSelf: 'flex-start' }}
            >
              Upload a video
            </Link>
          ) : (
            <Button
              type="button"
              variant="secondary"
              disabled={saving || !athleteId}
              loading={connecting === 'youtube'}
              onClick={() => {
                try { analytics.track('game_plan_video_connect_clicked', {}); } catch {}
                connect('youtube');
              }}
              style={{ alignSelf: 'flex-start' }}
            >
              Connect YouTube
            </Button>
          )}
        </section>
      </form>
    </Modal>
  );
};

export default AddVideoModal;
